(() => {
  const ROOT_SELECTOR = '[data-free-entries-offer]';
  const STORAGE_PREFIX = 'jrb:free-entries-offer:v2';
  const KEYS = {
    accepted: `${STORAGE_PREFIX}:accepted`,
    declined: `${STORAGE_PREFIX}:declined`,
    dismissed: `${STORAGE_PREFIX}:dismissed`,
    expired: `${STORAGE_PREFIX}:expired`,
    shownOnce: `${STORAGE_PREFIX}:shown-once`,
    countdownEnd: `${STORAGE_PREFIX}:countdown-end`,
    sessionId: `${STORAGE_PREFIX}:session-id`
  };
  const PRIVATE_PROPERTIES = {
    active: '_free_entries_offer',
    count: '_free_entries_count',
    expiresAt: '_free_entries_expires_at',
    claimedAt: '_free_entries_claimed_at',
    sessionId: '_free_entries_session_id'
  };

  class SafeSessionStorage {
    constructor() {
      this.memory = {};
      this.isAvailable = false;

      try {
        const probeKey = `${STORAGE_PREFIX}:probe`;
        window.sessionStorage.setItem(probeKey, '1');
        window.sessionStorage.removeItem(probeKey);
        this.isAvailable = true;
      } catch (error) {
        this.isAvailable = false;
      }
    }

    getItem(key) {
      if (this.isAvailable) {
        return window.sessionStorage.getItem(key);
      }

      return Object.prototype.hasOwnProperty.call(this.memory, key) ? this.memory[key] : null;
    }

    setItem(key, value) {
      if (this.isAvailable) {
        window.sessionStorage.setItem(key, value);
        return;
      }

      this.memory[key] = value;
    }

    removeItem(key) {
      if (this.isAvailable) {
        window.sessionStorage.removeItem(key);
        return;
      }

      delete this.memory[key];
    }
  }

  class FreeEntriesOfferController {
    constructor(root) {
      this.root = root;
      this.storage = new SafeSessionStorage();
      this.popupTimer = null;
      this.countdownTimer = null;
      this.syncDebounceTimer = null;
      this.reconcileDebounceTimer = null;
      this.rateLimitRetryTimer = null;
      this.chatSuppressionObserver = null;
      this.suppressedChatElements = new Set();
      this.pendingSyncForce = false;
      this.cartRequest = null;
      this.mutationRequest = null;
      this.cart = null;
      this.cartCount = Number.parseInt(root.dataset.cartCount || '0', 10);
      this.bonusLineItems = [];
      this.configuredBonusLineItems = [];
      this.rateLimitUntil = 0;
      this.lifecycleListenersBound = false;
      this.checkoutGuardListenersBound = false;
      this.handleResize = this.handleResize.bind(this);
      this.handleSectionLoad = this.handleSectionLoad.bind(this);
      this.handleCartRefresh = this.handleCartRefresh.bind(this);
      this.handleLineItemChange = this.handleLineItemChange.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleCheckoutSubmit = this.handleCheckoutSubmit.bind(this);
      this.handlePageShow = this.handlePageShow.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    }

    init() {
      if (this.root.dataset.initialized === 'true') {
        return;
      }

      this.root.dataset.initialized = 'true';
      this.shell = this.root.querySelector('[data-feo-shell]');
      this.popup = this.root.querySelector('[data-feo-popup]');
      this.widget = this.root.querySelector('[data-feo-widget]');
      this.timer = this.root.querySelector('[data-feo-timer]');
      this.expiredMessage = this.root.querySelector('[data-feo-expired-message]');
      this.popupText = this.root.querySelector('[data-feo-popup-text]');
      this.popupEyebrow = this.root.querySelector('[data-feo-eyebrow]');
      this.disclaimer = this.root.querySelector('[data-feo-disclaimer]');
      this.widgetLabel = this.root.querySelector('[data-feo-widget-label]');
      this.teaser = this.root.querySelector('[data-feo-teaser]');
      this.acceptButton = this.root.querySelector('[data-feo-accept]');
      this.declineButton = this.root.querySelector('[data-feo-decline]');
      this.dismissButton = this.root.querySelector('[data-feo-dismiss]');
      this.teaserOpenButton = this.root.querySelector('[data-feo-teaser-open]');
      this.syncShellStyle();
      this.portalShellToBody();

      this.config = this.readConfig();
      this.applyCopy();
      this.applyAnimationClass();
      this.bindCoreEvents();
      void this.bootstrap();
    }

    async bootstrap() {
      if (this.shouldBootstrapCartStateImmediately()) {
        await this.syncCartState({ force: true });
      } else {
        this.syncRuntimeListeners();
      }
      this.render();
    }

    destroy() {
      window.clearTimeout(this.popupTimer);
      window.clearInterval(this.countdownTimer);
      window.clearTimeout(this.syncDebounceTimer);
      window.clearTimeout(this.reconcileDebounceTimer);
      window.clearTimeout(this.rateLimitRetryTimer);
      this.setPopupActive(false);
      this.unbindLifecycleListeners();
      this.unbindCheckoutGuardListeners();
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('shopify:section:load', this.handleSectionLoad);
      this.restorePortaledShell();
      this.root.dataset.initialized = 'false';
    }

    syncShellStyle() {
      if (!this.shell) {
        return;
      }

      const rootStyle = this.root.getAttribute('style');

      if (rootStyle) {
        this.shell.setAttribute('style', rootStyle);
      }
    }

    portalShellToBody() {
      if (!this.shell || !document.body || this.shell.parentElement === document.body) {
        return;
      }

      this.originalShellParent = this.shell.parentNode;
      this.originalShellNextSibling = this.shell.nextSibling;
      this.shell.dataset.feoPortal = 'body';
      document.body.appendChild(this.shell);
    }

    restorePortaledShell() {
      if (!this.shell || this.shell.dataset.feoPortal !== 'body') {
        return;
      }

      delete this.shell.dataset.feoPortal;

      if (this.originalShellParent && this.originalShellParent.isConnected) {
        if (this.originalShellNextSibling && this.originalShellNextSibling.parentNode === this.originalShellParent) {
          this.originalShellParent.insertBefore(this.shell, this.originalShellNextSibling);
        } else {
          this.originalShellParent.appendChild(this.shell);
        }
        return;
      }

      this.shell.remove();
    }

    bindCoreEvents() {
      if (this.acceptButton) {
        this.acceptButton.addEventListener('click', () => {
          void this.acceptOffer();
        });
      }

      if (this.declineButton) {
        this.declineButton.addEventListener('click', () => this.declineOffer());
      }

      if (this.dismissButton) {
        this.dismissButton.addEventListener('click', () => this.dismissForSession());
      }

      if (this.teaserOpenButton) {
        this.teaserOpenButton.addEventListener('click', () => {
          void this.handleTeaserClick();
        });
      }

      window.addEventListener('resize', this.handleResize, { passive: true });
      document.addEventListener('shopify:section:load', this.handleSectionLoad);
    }

    bindLifecycleListeners() {
      if (this.lifecycleListenersBound) {
        return;
      }

      this.lifecycleListenersBound = true;
      window.addEventListener('pageshow', this.handlePageShow);
      document.addEventListener('cart:refresh', this.handleCartRefresh);
      document.addEventListener('cart:item-added', this.handleCartRefresh);
      document.addEventListener('line-item:change:end', this.handleLineItemChange);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    unbindLifecycleListeners() {
      if (!this.lifecycleListenersBound) {
        return;
      }

      this.lifecycleListenersBound = false;
      window.removeEventListener('pageshow', this.handlePageShow);
      document.removeEventListener('cart:refresh', this.handleCartRefresh);
      document.removeEventListener('cart:item-added', this.handleCartRefresh);
      document.removeEventListener('line-item:change:end', this.handleLineItemChange);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }

    bindCheckoutGuardListeners() {
      if (this.checkoutGuardListenersBound) {
        return;
      }

      this.checkoutGuardListenersBound = true;
      document.addEventListener('click', this.handleDocumentClick, true);
      document.addEventListener('submit', this.handleCheckoutSubmit, true);
    }

    unbindCheckoutGuardListeners() {
      if (!this.checkoutGuardListenersBound) {
        return;
      }

      this.checkoutGuardListenersBound = false;
      document.removeEventListener('click', this.handleDocumentClick, true);
      document.removeEventListener('submit', this.handleCheckoutSubmit, true);
    }

    syncRuntimeListeners() {
      if (this.shouldMaintainOfferSync()) {
        this.bindLifecycleListeners();
      } else {
        this.unbindLifecycleListeners();
      }

      if (this.isCheckoutBlocked()) {
        this.bindCheckoutGuardListeners();
      } else {
        this.unbindCheckoutGuardListeners();
      }
    }

    shouldBootstrapCartStateImmediately() {
      const state = this.getState();
      return this.cartCount > 0 || (state.accepted && state.countdownEnd > 0);
    }

    readConfig() {
      const popupEyebrowText = this.root.getAttribute('data-popup-eyebrow-text');

      return {
        enabled: this.root.dataset.enabled === 'true',
        pageType: this.root.dataset.pageType || '',
        pagePath: this.root.dataset.pagePath || '/',
        delaySeconds: Number.parseInt(this.root.dataset.delaySeconds || '4', 10),
        countdownMinutes: Number.parseInt(this.root.dataset.countdownMinutes || '10', 10),
        entryCount: Number.parseInt(this.root.dataset.entryCount || '25', 10),
        expiredBehavior: this.root.dataset.expiredBehavior || 'hide',
        teaserClickBehavior: this.root.dataset.teaserClickBehavior || 'reopen_popup',
        requireCartItems: this.root.dataset.requireCartItems === 'true',
        oncePerSession: this.root.dataset.oncePerSession === 'true',
        showDesktop: this.root.dataset.showDesktop === 'true',
        showMobile: this.root.dataset.showMobile === 'true',
        pageScope: this.root.dataset.pageScope || 'all_pages',
        showHome: this.root.dataset.showHome === 'true',
        showProduct: this.root.dataset.showProduct === 'true',
        showCollection: this.root.dataset.showCollection === 'true',
        showPage: this.root.dataset.showPage === 'true',
        showArticle: this.root.dataset.showArticle === 'true',
        showBlog: this.root.dataset.showBlog === 'true',
        showCart: this.root.dataset.showCart === 'true',
        showSearch: this.root.dataset.showSearch === 'true',
        excludedPaths: this.root.dataset.excludedPaths || '',
        popupText: this.root.dataset.popupText || 'Get [[entry_count]] number of free entries if you checkout in [[countdown_minutes]] number of minutes',
        teaserText: this.root.dataset.teaserText || 'Get [[entry_count]] number of free entries',
        acceptedText: this.root.dataset.acceptedText || 'Your free entries are reserved for:',
        expiredText: this.root.dataset.expiredText || 'This offer has expired',
        disclaimerText: this.root.dataset.disclaimerText || '',
        popupEyebrowText: popupEyebrowText === null ? '[[entry_count]] FREE ENTRIES' : popupEyebrowText,
        bonusVariantId: Number.parseInt(this.root.dataset.bonusVariantId || '0', 10),
        animationEnabled: this.root.dataset.animationEnabled === 'true',
        animationStyle: this.root.dataset.animationStyle || 'slide-up'
      };
    }

    applyCopy() {
      const popupText = this.interpolateText(this.config.popupText);
      const teaserText = this.interpolateText(this.config.teaserText);
      const acceptedText = this.interpolateText(this.config.acceptedText);
      const expiredText = this.interpolateText(this.config.expiredText);
      const disclaimerText = this.interpolateText(this.config.disclaimerText);
      const popupEyebrowText = this.interpolateText(this.config.popupEyebrowText);

      if (this.popupEyebrow) {
        if (popupEyebrowText.trim().length > 0) {
          this.popupEyebrow.textContent = popupEyebrowText;
          this.showElement(this.popupEyebrow);
        } else {
          this.hideElement(this.popupEyebrow);
        }
      }

      if (this.popupText) {
        this.popupText.textContent = popupText;
      }

      if (this.teaserOpenButton) {
        this.teaserOpenButton.textContent = teaserText;
      }

      if (this.widgetLabel) {
        this.widgetLabel.textContent = acceptedText;
      }

      if (this.expiredMessage) {
        this.expiredMessage.textContent = expiredText;
      }

      if (this.disclaimer) {
        if (disclaimerText.trim().length > 0) {
          this.disclaimer.innerHTML = this.escapeHtml(disclaimerText).replace(/\n/g, '<br>');
          this.showElement(this.disclaimer);
        } else {
          this.hideElement(this.disclaimer);
        }
      }
    }

    interpolateText(value) {
      return value
        .replace(/\{\{\s*entry_count\s*\}\}/g, String(this.config.entryCount))
        .replace(/\{\{\s*countdown_minutes\s*\}\}/g, String(this.config.countdownMinutes))
        .replace(/\[\[\s*entry_count\s*\]\]/g, String(this.config.entryCount))
        .replace(/\[\[\s*countdown_minutes\s*\]\]/g, String(this.config.countdownMinutes));
    }

    escapeHtml(value) {
      const node = document.createElement('div');
      node.textContent = value;
      return node.innerHTML;
    }

    applyAnimationClass() {
      if (!this.shell) {
        return;
      }

      this.shell.classList.remove('has-animations', 'animation-slide-up', 'animation-slide-left', 'animation-fade');

      if (!this.config.animationEnabled || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }

      this.shell.classList.add('has-animations', `animation-${this.config.animationStyle}`);
    }

    async handleResize() {
      this.render();
    }

    async handleSectionLoad(event) {
      const targetRoot = event.target.matches && event.target.matches(ROOT_SELECTOR)
        ? event.target
        : event.target.querySelector && event.target.querySelector(ROOT_SELECTOR);

      if (targetRoot && targetRoot === this.root) {
        this.config = this.readConfig();
        this.syncShellStyle();
        this.applyCopy();
        this.applyAnimationClass();
        await this.syncCartState();
        this.render();
      }
    }

    handleCartRefresh(event) {
      if (event && event.detail && event.detail.source === 'free-entries-offer') {
        return;
      }

      const cart = event && event.detail && event.detail.cart && Array.isArray(event.detail.cart.items)
        ? event.detail.cart
        : null;

      if (cart) {
        this.applyCartState(cart);
        this.render();
        this.scheduleReconcile();
        return;
      }

      if (this.shouldMaintainOfferSync()) {
        this.scheduleSync(false);
      }
    }

    handleLineItemChange(event) {
      if (event && event.detail && event.detail.source === 'free-entries-offer') {
        return;
      }

      const cart = event && event.detail && event.detail.cart && Array.isArray(event.detail.cart.items)
        ? event.detail.cart
        : null;

      if (cart) {
        this.applyCartState(cart);
        this.render();
        this.scheduleReconcile();
        return;
      }

      if (this.shouldMaintainOfferSync()) {
        this.scheduleSync(false);
      }
    }

    handlePageShow() {
      if (this.hasActiveOfferState()) {
        this.scheduleSync(false);
      }
    }

    handleVisibilityChange() {
      if (document.visibilityState === 'visible' && this.hasActiveOfferState()) {
        this.scheduleSync(false);
      }
    }

    getCartUrl() {
      if (window.theme && window.theme.routes && window.theme.routes.cart_url) {
        return `${window.theme.routes.cart_url}.js`;
      }

      return '/cart.js';
    }

    async fetchCart(options = {}) {
      if (this.cartRequest && !options.force) {
        return this.cartRequest;
      }

      if (this.getRateLimitRemaining() > 0) {
        this.scheduleRateLimitRetry();
        return this.cart;
      }

      this.cartRequest = window.fetch(this.getCartUrl(), {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      })
        .then((response) => {
          if (response.status === 429) {
            this.startRateLimitCooldown();
            throw new Error('cart request rate limited');
          }

          if (!response.ok) {
            throw new Error('cart request failed');
          }

          return response.json();
        })
        .finally(() => {
          this.cartRequest = null;
        });

      return this.cartRequest;
    }

    applyCartState(cart) {
      this.cart = cart;
      this.cartCount = typeof cart.item_count === 'number' ? cart.item_count : this.cartCount;
      this.root.dataset.cartCount = String(this.cartCount);
      this.configuredBonusLineItems = this.getConfiguredBonusLineItems(cart);
      this.bonusLineItems = this.getBonusLineItems(cart);
      this.syncRuntimeListeners();
    }

    async syncCartState(options = {}) {
      try {
        const cart = await this.fetchCart(options);

        if (!cart || !Array.isArray(cart.items)) {
          return this.cart;
        }

        this.applyCartState(cart);
        await this.reconcileStateWithCart();
        return cart;
      } catch (error) {
        return null;
      }
    }

    getState() {
      return {
        accepted: this.storage.getItem(KEYS.accepted) === 'true',
        declined: this.storage.getItem(KEYS.declined) === 'true',
        dismissed: this.storage.getItem(KEYS.dismissed) === 'true',
        expired: this.storage.getItem(KEYS.expired) === 'true',
        shownOnce: this.storage.getItem(KEYS.shownOnce) === 'true',
        countdownEnd: Number.parseInt(this.storage.getItem(KEYS.countdownEnd) || '0', 10)
      };
    }

    hasActiveOfferState() {
      const state = this.getState();
      return state.accepted && state.countdownEnd > Date.now();
    }

    shouldMaintainOfferSync() {
      return this.hasActiveOfferState() || Boolean((this.configuredBonusLineItems || []).length);
    }

    getRateLimitRemaining() {
      return Math.max(this.rateLimitUntil - Date.now(), 0);
    }

    startRateLimitCooldown(duration = 2500) {
      this.rateLimitUntil = Math.max(this.rateLimitUntil, Date.now() + duration);
      this.scheduleRateLimitRetry();
    }

    scheduleRateLimitRetry() {
      if (!this.shouldMaintainOfferSync() || this.rateLimitRetryTimer) {
        return;
      }

      const delay = this.getRateLimitRemaining();

      this.rateLimitRetryTimer = window.setTimeout(() => {
        this.rateLimitRetryTimer = null;
        this.scheduleSync(false);
      }, Math.max(delay, 0) + 60);
    }

    scheduleReconcile() {
      window.clearTimeout(this.reconcileDebounceTimer);

      this.reconcileDebounceTimer = window.setTimeout(async () => {
        this.reconcileDebounceTimer = null;

        if (this.mutationRequest) {
          this.scheduleReconcile();
          return;
        }

        await this.reconcileStateWithCart();
        this.render();
      }, 140);
    }

    scheduleSync(force = false) {
      this.pendingSyncForce = this.pendingSyncForce || force;
      window.clearTimeout(this.syncDebounceTimer);

      this.syncDebounceTimer = window.setTimeout(async () => {
        const useForce = this.pendingSyncForce;
        this.pendingSyncForce = false;
        this.syncDebounceTimer = null;

        if (this.mutationRequest) {
          this.scheduleSync(useForce);
          return;
        }

        await this.syncCartState({ force: useForce });
        this.render();
      }, Math.max(180, this.getRateLimitRemaining()));
    }

    setState(nextState) {
      Object.entries(nextState).forEach(([key, value]) => {
        const storageKey = KEYS[key];

        if (!storageKey) {
          return;
        }

        if (value === null || value === false || value === '') {
          this.storage.removeItem(storageKey);
          return;
        }

        this.storage.setItem(storageKey, String(value));
      });

      this.syncRuntimeListeners();
    }

    getSessionId() {
      let sessionId = this.storage.getItem(KEYS.sessionId);

      if (sessionId) {
        return sessionId;
      }

      sessionId = `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
      this.storage.setItem(KEYS.sessionId, sessionId);
      return sessionId;
    }

    getConfiguredBonusLineItems(cart) {
      if (!this.config.bonusVariantId || !cart || !Array.isArray(cart.items)) {
        return [];
      }

      return cart.items.filter((item) => Number(item.variant_id) === this.config.bonusVariantId);
    }

    getBonusLineItems(cart) {
      return this.getConfiguredBonusLineItems(cart).filter((item) => {
        const properties = item.properties || {};
        return properties[PRIVATE_PROPERTIES.active] === 'true';
      });
    }

    getQualifyingLineItems(cart) {
      if (!cart || !Array.isArray(cart.items)) {
        return [];
      }

      return cart.items.filter((item) => Number(item.variant_id) !== this.config.bonusVariantId);
    }

    hasQualifyingCartItems(cart) {
      return this.getQualifyingLineItems(cart).some((item) => Number(item.quantity || 0) > 0);
    }

    getBonusExpiry(item) {
      if (!item || !item.properties) {
        return 0;
      }

      const expiresAt = item.properties[PRIVATE_PROPERTIES.expiresAt];
      const timestamp = expiresAt ? Date.parse(expiresAt) : NaN;
      return Number.isNaN(timestamp) ? 0 : timestamp;
    }

    getLatestKnownExpiry(configuredBonusLineItems, countdownEnd) {
      const lineExpiries = (configuredBonusLineItems || [])
        .map((item) => this.getBonusExpiry(item))
        .filter((value) => value > 0);

      if (lineExpiries.length) {
        return Math.min(countdownEnd || Number.MAX_SAFE_INTEGER, ...lineExpiries);
      }

      return countdownEnd || 0;
    }

    buildBonusProperties(countdownEnd) {
      return {
        [PRIVATE_PROPERTIES.active]: 'true',
        [PRIVATE_PROPERTIES.count]: String(this.config.entryCount),
        [PRIVATE_PROPERTIES.expiresAt]: new Date(countdownEnd).toISOString(),
        [PRIVATE_PROPERTIES.claimedAt]: new Date().toISOString(),
        [PRIVATE_PROPERTIES.sessionId]: this.getSessionId()
      };
    }

    areBonusPropertiesEqual(currentProperties, nextProperties) {
      const current = currentProperties || {};
      return Object.keys(nextProperties).every((key) => String(current[key] || '') === String(nextProperties[key]));
    }

    async reconcileStateWithCart() {
      if (!this.config.bonusVariantId) {
        this.updateCheckoutGuard();
        return;
      }

      const state = this.getState();
      const configuredBonusLineItems = this.configuredBonusLineItems || [];
      const countdownEnd = state.countdownEnd;
      const effectiveExpiry = this.getLatestKnownExpiry(configuredBonusLineItems, countdownEnd);
      const offerIsActive = state.accepted && effectiveExpiry > Date.now();

      if (configuredBonusLineItems.length && effectiveExpiry && effectiveExpiry <= Date.now()) {
        await this.removeBonusLineItems({
          lineItems: configuredBonusLineItems,
          preserveExpiredState: true,
          silentRefresh: false
        });

        this.setState({
          accepted: false,
          declined: false,
          dismissed: false,
          expired: true,
          countdownEnd: null
        });
        this.updateCheckoutGuard();
        return;
      }

      if (offerIsActive) {
        if (this.hasQualifyingCartItems(this.cart)) {
          const syncResult = await this.ensureBonusLineItem(countdownEnd);

          if (syncResult === false) {
            this.updateCheckoutGuard();
            return;
          }

          if (syncResult === 'mutated') {
            return;
          }
        } else if (configuredBonusLineItems.length) {
          await this.removeBonusLineItems({
            lineItems: configuredBonusLineItems,
            preserveExpiredState: true,
            preserveOfferState: true
          });
          return;
        } else {
          this.updateCheckoutGuard();
          return;
        }
      }

      if (!configuredBonusLineItems.length) {
        if (state.accepted && (!countdownEnd || countdownEnd <= Date.now())) {
          this.setState({
            accepted: false,
            countdownEnd: null
          });
        }
        this.updateCheckoutGuard();
        return;
      }

      if (configuredBonusLineItems.length > 1) {
        await this.removeBonusLineItems({
          lineItems: configuredBonusLineItems.slice(1),
          preserveExpiredState: true,
          silentRefresh: true
        });
        this.updateCheckoutGuard();
        return;
      }

      const primaryLineItem = configuredBonusLineItems[0];
      const expiresAt = this.getBonusExpiry(primaryLineItem);

      if (expiresAt && expiresAt <= Date.now()) {
        await this.removeBonusLineItems({
          lineItems: configuredBonusLineItems,
          preserveExpiredState: true,
          silentRefresh: false
        });

        this.setState({
          accepted: false,
          declined: false,
          dismissed: false,
          expired: true,
          countdownEnd: null
        });
        this.updateCheckoutGuard();
        return;
      }

      if (primaryLineItem.quantity !== 1) {
        await this.changeLineItem(primaryLineItem.key, 1, primaryLineItem.properties || {});
        await this.syncCartState();
        return;
      }

      this.setState({
        accepted: true,
        declined: false,
        dismissed: false,
        expired: false,
        shownOnce: true,
        countdownEnd: expiresAt || state.countdownEnd
      });
      this.updateCheckoutGuard();
    }

    isDeviceEligible() {
      const isMobile = window.matchMedia('(max-width: 767px)').matches;

      if (isMobile) {
        return this.config.showMobile;
      }

      return this.config.showDesktop;
    }

    isEligibleForInitialOffer() {
      if (!this.config.enabled || !this.isPageEligible() || !this.isDeviceEligible()) {
        return false;
      }

      return true;
    }

    render() {
      const state = this.getState();
      const popupWasVisible = this.popup && this.popup.classList.contains('is-visible');

      this.resetVisibility();
      this.updateCheckoutGuard();
      this.syncRuntimeListeners();

      if (!this.config.enabled || !this.isPageEligible() || !this.isDeviceEligible() || state.dismissed) {
        return;
      }

      if (state.accepted && state.countdownEnd > Date.now()) {
        this.showAcceptedWidget();
        this.startCountdown(state.countdownEnd);
        return;
      }

      if (state.accepted && state.countdownEnd <= Date.now()) {
        void this.expireOffer();
        return;
      }

      if (state.expired) {
        if (this.config.expiredBehavior === 'show_message') {
          this.showExpiredWidget();
        }

        return;
      }

      if (state.declined) {
        this.showTeaser();
        return;
      }

      if (!this.isEligibleForInitialOffer()) {
        return;
      }

      if (popupWasVisible) {
        this.showPopup();
        return;
      }

      if (this.config.oncePerSession && state.shownOnce) {
        return;
      }

      this.schedulePopup();
    }

    schedulePopup() {
      if (this.popupTimer || this.popup.classList.contains('is-visible')) {
        return;
      }

      const delay = Math.max(this.config.delaySeconds, 0) * 1000;

      this.popupTimer = window.setTimeout(() => {
        this.popupTimer = null;
        this.setState({ shownOnce: true });
        this.showPopup();
      }, delay);
    }

    setPopupActive(isActive) {
      document.documentElement.classList.toggle('jrb-feo-popup-active', isActive);

      if (document.body) {
        document.body.classList.toggle('jrb-feo-popup-active', isActive);
      }

      this.setChatLauncherSuppressed(isActive);
    }

    setChatLauncherSuppressed(isSuppressed) {
      if (!isSuppressed) {
        this.stopChatSuppressionObserver();
        this.restoreSuppressedChatElements();
        return;
      }

      this.suppressChatElements();

      if (!this.chatSuppressionObserver && document.body && window.MutationObserver) {
        this.chatSuppressionObserver = new MutationObserver(() => this.suppressChatElements());
        this.chatSuppressionObserver.observe(document.body, {
          childList: true,
          subtree: true
        });
      }
    }

    suppressChatElements() {
      document.querySelectorAll('.chat-app, .chat-toggle, .chat-app [data-spec="toggle-button"]').forEach((element) => {
        if (element.closest && element.closest('.jrb-feo')) {
          return;
        }

        element.dataset.feoChatSuppressed = 'true';
        element.style.setProperty('display', 'none', 'important');
        element.style.setProperty('visibility', 'hidden', 'important');
        element.style.setProperty('pointer-events', 'none', 'important');
        element.style.setProperty('z-index', '1', 'important');
        this.suppressedChatElements.add(element);
      });
    }

    stopChatSuppressionObserver() {
      if (!this.chatSuppressionObserver) {
        return;
      }

      this.chatSuppressionObserver.disconnect();
      this.chatSuppressionObserver = null;
    }

    restoreSuppressedChatElements() {
      this.suppressedChatElements.forEach((element) => {
        if (!element || element.dataset.feoChatSuppressed !== 'true') {
          return;
        }

        element.style.removeProperty('display');
        element.style.removeProperty('visibility');
        element.style.removeProperty('pointer-events');
        element.style.removeProperty('z-index');
        delete element.dataset.feoChatSuppressed;
      });

      this.suppressedChatElements.clear();
    }

    showPopup() {
      this.root.classList.remove('is-accepted', 'is-declined', 'is-expired');
      this.setPopupActive(true);
      this.showElement(this.popup);
      this.hideElement(this.widget);
      this.hideElement(this.teaser);
    }

    showAcceptedWidget() {
      this.root.classList.add('is-accepted');
      this.root.classList.remove('is-declined', 'is-expired');
      this.setPopupActive(false);
      this.hideElement(this.popup);
      this.hideElement(this.teaser);
      this.hideElement(this.expiredMessage);
      this.showElement(this.widget);
    }

    showExpiredWidget() {
      window.clearInterval(this.countdownTimer);
      this.root.classList.add('is-expired');
      this.root.classList.remove('is-accepted', 'is-declined');
      this.setPopupActive(false);
      this.hideElement(this.popup);
      this.hideElement(this.teaser);
      this.showElement(this.widget);
      this.hideElement(this.timer);
      this.showElement(this.expiredMessage);
    }

    showTeaser() {
      this.root.classList.add('is-declined');
      this.root.classList.remove('is-accepted', 'is-expired');
      this.setPopupActive(false);
      this.hideElement(this.popup);
      this.hideElement(this.widget);
      this.showElement(this.teaser);
    }

    async acceptOffer() {
      if (!this.cart) {
        await this.syncCartState();
      }

      const countdownEnd = Date.now() + (this.config.countdownMinutes * 60 * 1000);
      const hasQualifyingCartItems = this.hasQualifyingCartItems(this.cart);

      if (this.config.bonusVariantId && hasQualifyingCartItems) {
        const added = await this.ensureBonusLineItem(countdownEnd);
        if (!added) {
          return;
        }
      }

      this.setState({
        accepted: true,
        declined: false,
        dismissed: false,
        expired: false,
        shownOnce: true,
        countdownEnd
      });

      document.dispatchEvent(new CustomEvent('jrb:free-entries-offer:accepted', {
        detail: {
          entryCount: this.config.entryCount,
          countdownEnd
        }
      }));

      this.showAcceptedWidget();
      this.startCountdown(countdownEnd);
    }

    isCheckoutBlocked() {
      return Boolean((this.configuredBonusLineItems || []).length) && !this.hasQualifyingCartItems(this.cart);
    }

    updateCheckoutGuard() {
      const isBlocked = this.isCheckoutBlocked();
      const guardMessage = 'Add at least one paid product to the cart before checkout. FREE-ENTRIES cannot be checked out by itself.';

      document.querySelectorAll('[data-free-entries-checkout-guard]').forEach((element) => {
        element.hidden = !isBlocked;
        element.textContent = guardMessage;
      });

      document.querySelectorAll('button[name="checkout"]').forEach((button) => {
        button.disabled = isBlocked;
        button.setAttribute('aria-disabled', isBlocked ? 'true' : 'false');
        button.classList.toggle('is-disabled', isBlocked);
      });

      document.querySelectorAll('[data-free-entries-dynamic-checkout]').forEach((element) => {
        element.hidden = isBlocked;
      });

      document.documentElement.classList.toggle('jrb-free-entries-checkout-blocked', isBlocked);
      this.syncRuntimeListeners();
    }

    focusCheckoutGuard() {
      const firstGuard = document.querySelector('[data-free-entries-checkout-guard]:not([hidden])');

      if (!firstGuard) {
        return;
      }

      firstGuard.setAttribute('tabindex', '-1');
      firstGuard.focus();
    }

    handleDocumentClick(event) {
      const checkoutButton = event.target.closest && event.target.closest('button[name="checkout"]');

      if (!checkoutButton || !this.isCheckoutBlocked()) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.focusCheckoutGuard();
    }

    handleCheckoutSubmit(event) {
      const submitter = event.submitter;

      if (!submitter || !submitter.matches || !submitter.matches('button[name="checkout"]') || !this.isCheckoutBlocked()) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.focusCheckoutGuard();
    }

    declineOffer() {
      this.setState({
        accepted: false,
        declined: true,
        dismissed: false,
        expired: false,
        shownOnce: true,
        countdownEnd: null
      });

      document.dispatchEvent(new CustomEvent('jrb:free-entries-offer:declined'));
      this.showTeaser();
    }

    dismissForSession() {
      this.setState({
        accepted: false,
        declined: false,
        dismissed: true,
        expired: false,
        shownOnce: true,
        countdownEnd: null
      });

      document.dispatchEvent(new CustomEvent('jrb:free-entries-offer:dismissed'));
      this.resetVisibility();
    }

    async handleTeaserClick() {
      if (this.config.teaserClickBehavior === 'accept_offer') {
        await this.acceptOffer();
        return;
      }

      this.showPopup();
    }

    async expireOffer() {
      await this.removeBonusLineItems({
        preserveExpiredState: true
      });

      this.setState({
        accepted: false,
        declined: false,
        dismissed: false,
        expired: true,
        countdownEnd: null
      });

      document.dispatchEvent(new CustomEvent('jrb:free-entries-offer:expired'));
      this.render();
    }

    startCountdown(countdownEnd) {
      window.clearInterval(this.countdownTimer);
      this.updateTimer(countdownEnd);

      this.countdownTimer = window.setInterval(() => {
        this.updateTimer(countdownEnd);
      }, 1000);
    }

    updateTimer(countdownEnd) {
      const remaining = countdownEnd - Date.now();

      if (remaining <= 0) {
        window.clearInterval(this.countdownTimer);
        void this.expireOffer();
        return;
      }

      if (this.timer) {
        this.showElement(this.timer);
        this.timer.textContent = this.formatRemainingTime(remaining);
      }
    }

    formatRemainingTime(milliseconds) {
      const totalSeconds = Math.max(Math.floor(milliseconds / 1000), 0);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      const paddedMinutes = String(minutes).padStart(2, '0');
      const paddedSeconds = String(seconds).padStart(2, '0');

      if (hours > 0) {
        return `${hours}:${paddedMinutes}:${paddedSeconds}`;
      }

      return `${String(minutes).padStart(2, '0')}:${paddedSeconds}`;
    }

    isPageEligible() {
      if (this.config.pageScope === 'all_pages') {
        return !this.isPathExcluded();
      }

      const pageTypeMap = {
        index: this.config.showHome,
        product: this.config.showProduct,
        collection: this.config.showCollection,
        page: this.config.showPage,
        article: this.config.showArticle,
        blog: this.config.showBlog,
        cart: this.config.showCart,
        search: this.config.showSearch
      };

      return Boolean(pageTypeMap[this.config.pageType]) && !this.isPathExcluded();
    }

    isPathExcluded() {
      const entries = this.config.excludedPaths
        .split(/\r?\n/)
        .map((value) => value.trim())
        .filter(Boolean);

      if (!entries.length) {
        return false;
      }

      return entries.some((entry) => this.config.pagePath === entry || this.config.pagePath.includes(entry));
    }

    async ensureBonusLineItem(countdownEnd) {
      const properties = this.buildBonusProperties(countdownEnd);
      let cart = this.cart;
      let changed = false;

      if (!cart) {
        try {
          cart = await this.fetchCart();
          this.applyCartState(cart);
        } catch (error) {
          return false;
        }
      }

      if (this.bonusLineItems.length > 1) {
        await this.removeBonusLineItems({
          lineItems: this.bonusLineItems.slice(1),
          preserveExpiredState: true,
          silentRefresh: true
        });
      }

      const primaryLineItem = this.bonusLineItems[0];

      if (!primaryLineItem) {
        const added = await this.addBonusLineItem(properties);
        if (!added) {
          return false;
        }
        changed = true;
      } else {
        const needsUpdate = primaryLineItem.quantity !== 1 || !this.areBonusPropertiesEqual(primaryLineItem.properties, properties);

        if (needsUpdate) {
          const lineItemUpdated = await this.changeLineItem(primaryLineItem.key, 1, properties);
          if (!lineItemUpdated) {
            return false;
          }
          changed = true;
        }
      }

      if (changed) {
        if (!primaryLineItem) {
          await this.syncCartState({ force: true });
        }
        this.notifyCartRefresh();
        return 'mutated';
      }

      return 'noop';
    }

    async addBonusLineItem(properties) {
      if (this.getRateLimitRemaining() > 0) {
        this.scheduleRateLimitRetry();
        return false;
      }

      return this.runMutation(async () => {
        try {
          const response = await window.fetch(`${theme.routes.cart_add_url}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              items: [{
                id: this.config.bonusVariantId,
                quantity: 1,
                properties
              }]
            })
          });

          if (response.status === 429) {
            this.startRateLimitCooldown();
            return false;
          }

          if (!response.ok) {
            return false;
          }

          await response.json();
          return true;
        } catch (error) {
          return false;
        }
      });
    }

    async changeLineItem(id, quantity, properties) {
      if (this.getRateLimitRemaining() > 0) {
        this.scheduleRateLimitRetry();
        return false;
      }

      return this.runMutation(async () => {
        try {
          const body = {
            id,
            quantity
          };

          if (properties) {
            body.properties = properties;
          }

          const response = await window.fetch(theme.routes.cart_change_url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify(body)
          });

          if (response.status === 429) {
            this.startRateLimitCooldown();
            return false;
          }

          if (!response.ok) {
            return false;
          }

          const parsedState = JSON.parse(await response.text());

          if (parsedState.errors) {
            return false;
          }

          this.applyCartState(parsedState);
          return true;
        } catch (error) {
          return false;
        }
      });
    }

    async removeBonusLineItems(options = {}) {
      const targets = options.lineItems || this.configuredBonusLineItems || this.bonusLineItems;

      if (!targets || !targets.length) {
        if (!options.silentRefresh) {
          this.notifyCartRefresh();
        }
        return true;
      }

      for (const item of targets) {
        const removed = await this.changeLineItem(item.key, 0);

        if (!removed) {
          return false;
        }
      }

      if (!options.silentRefresh) {
        this.notifyCartRefresh();
      }

      if (!options.preserveExpiredState && !options.preserveOfferState) {
        this.setState({
          accepted: false,
          countdownEnd: null
        });
      }

      return true;
    }

    runMutation(callback) {
      if (this.mutationRequest) {
        return this.mutationRequest;
      }

      this.mutationRequest = Promise.resolve(callback())
        .finally(() => {
          this.mutationRequest = null;
        });

      return this.mutationRequest;
    }

    notifyCartRefresh() {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: {
          cart: this.cart,
          source: 'free-entries-offer'
        }
      }));
    }

    showElement(element) {
      if (!element) {
        return;
      }

      element.hidden = false;
      element.classList.add('is-visible');
      element.classList.remove('is-hidden');
    }

    hideElement(element) {
      if (!element) {
        return;
      }

      element.hidden = true;
      element.classList.add('is-hidden');
      element.classList.remove('is-visible');
    }

    resetVisibility() {
      window.clearTimeout(this.popupTimer);
      this.popupTimer = null;
      window.clearInterval(this.countdownTimer);
      this.setPopupActive(false);
      this.hideElement(this.popup);
      this.hideElement(this.widget);
      this.hideElement(this.teaser);
      this.hideElement(this.expiredMessage);
      this.root.classList.remove('is-accepted', 'is-declined', 'is-expired');
    }
  }

  const mount = () => {
    const root = document.querySelector(ROOT_SELECTOR);

    if (!root) {
      return;
    }

    if (window.jrbFreeEntriesOfferController) {
      window.jrbFreeEntriesOfferController.destroy();
    }

    window.jrbFreeEntriesOfferController = new FreeEntriesOfferController(root);
    window.jrbFreeEntriesOfferController.init();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }

  document.addEventListener('shopify:section:load', mount);
})();
